import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { SEO } from '../../components/SEO';
import {
  TreePine, MapPin, Calendar, Clock, ExternalLink, Loader2,
  GraduationCap, Filter, Users
} from 'lucide-react';

const PD_COLORS = {
  'Plainfield Park District': { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  'Batavia Park District': { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  'Oswegoland Park District': { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  'Geneva Park District': { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  'St Charles Park District': { bg: 'bg-rose-50', border: 'border-rose-200', accent: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' },
};

export default function ParkDistrictCampsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPD, setSelectedPD] = useState('all');

  useEffect(() => {
    fetch('/pd_schedule.json')
      .then(r => r.json())
      .then(d => setEvents(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const parkDistricts = [...new Set(events.map(e => e.park_district))];
  const filtered = selectedPD === 'all' ? events : events.filter(e => e.park_district === selectedPD);

  // Group by park district, then by date range
  const grouped = {};
  filtered.forEach(e => {
    const key = `${e.park_district}|${e.dates}`;
    if (!grouped[key]) {
      grouped[key] = { park_district: e.park_district, venue: e.venue, dates: e.dates, time: e.time, grade: e.grade, courses: [], registration_url: e.registration_url };
    }
    grouped[key].courses.push(e);
    if (e.registration_url && !grouped[key].registration_url) grouped[key].registration_url = e.registration_url;
  });

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-rsc-navy" /></div>;
  }

  return (
    <div data-testid="pd-camps-page">
      <SEO title="Park District Summer Camps" description="STEM summer camps at partner park districts across the Fox Valley region. Oswegoland, Plainfield, Geneva, Batavia, and St. Charles." />

      {/* Hero */}
      <section className="relative py-14 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 text-sm font-medium mb-4">
            <TreePine className="w-4 h-4" /> PARTNER PARK DISTRICTS
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] mb-3">
            STEM Camps at Your <span className="text-rsc-yellow">Local Park District</span>
          </h1>
          <p className="text-base text-white/60 max-w-xl mb-4">
            The same Ready Set STEM curriculum — at a park district near you. Register directly through your park district.
          </p>
          <div className="flex gap-4">
            <div className="text-center"><p className="text-2xl font-bold text-rsc-yellow">{parkDistricts.length}</p><p className="text-xs text-white/40">Park Districts</p></div>
            <div className="text-center"><p className="text-2xl font-bold text-white">{events.length}</p><p className="text-xs text-white/40">Sessions Available</p></div>
          </div>
        </div>
      </section>

      {/* Filters + Content */}
      <section className="py-8 bg-gray-50 min-h-[50vh]">
        <div className="container-app">
          {/* PD Filter */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="pd-filters">
            <Button variant={selectedPD === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setSelectedPD('all')}
              className={selectedPD === 'all' ? 'bg-rsc-navy' : ''}>
              All Districts ({events.length})
            </Button>
            {parkDistricts.map(pd => {
              const count = events.filter(e => e.park_district === pd).length;
              const color = PD_COLORS[pd] || PD_COLORS['Plainfield Park District'];
              return (
                <Button key={pd} variant={selectedPD === pd ? 'default' : 'outline'} size="sm" onClick={() => setSelectedPD(pd)}
                  className={selectedPD === pd ? 'bg-rsc-navy' : ''}>
                  <TreePine className="w-3.5 h-3.5 mr-1.5" /> {pd.replace(' Park District', '')} ({count})
                </Button>
              );
            })}
          </div>

          {/* Grouped camp cards */}
          <div className="space-y-4">
            {Object.values(grouped).map((group, i) => {
              const color = PD_COLORS[group.park_district] || PD_COLORS['Plainfield Park District'];
              return (
                <Card key={i} className={`${color.border} ${color.bg} overflow-hidden`} data-testid={`pd-camp-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${color.badge} text-xs`}>
                            <TreePine className="w-3 h-3 mr-1" /> {group.park_district}
                          </Badge>
                          <Badge variant="outline" className="text-xs">{group.grade}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {group.dates}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {group.time}</span>
                          <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {group.venue}</span>
                        </div>
                      </div>
                      {group.registration_url ? (
                        <a href={group.registration_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="bg-rsc-navy text-white font-bold rounded-full">
                            Register <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                          </Button>
                        </a>
                      ) : (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Registration Opens Soon</Badge>
                      )}
                    </div>

                    {/* Course list */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                      {group.courses.map((course, j) => (
                        <div key={j} className="p-3 bg-white rounded-lg border border-gray-100">
                          <p className="text-xs text-gray-400 font-bold uppercase mb-0.5">{course.days}</p>
                          <p className="text-sm font-semibold text-gray-900 leading-tight">{course.title}</p>
                          {course.registration_url && course.registration_url !== group.registration_url && (
                            <a href={course.registration_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline mt-1 inline-block">
                              Register for this course <ExternalLink className="w-2.5 h-2.5 inline" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12">
              <TreePine className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No sessions available for this park district.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
